import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { apiRequest, setStoredToken } from "../api/client";
import { Button } from "../components/Button/Button";
import { InlineNotice } from "../components/Feedback/InlineNotice";
import { PasswordInput } from "../components/PasswordInput/PasswordInput";
import { authError, isValidEmail, normalizeEmail, normalizePhone, passwordRules } from "../auth/format";
import { useOtpCooldown } from "../auth/useOtpCooldown";
import {
  billingSignupSchema,
  type AuthResponse,
  type BillingSignupPayload,
  type BillingSignupValues,
  type CurrentLegalDocumentsResponse,
  type OtpRequestResponse,
  type OtpVerifyResponse,
  type VerifiedOtp,
} from "../auth/types";
import "./BillingLandingPage.css";

const EMPTY_VERIFICATION: VerifiedOtp = { destination: "", token: "" };

interface BillingSignupStatus {
  product_name: string;
  public_signup_available: boolean;
  mode: "public" | "internal" | "closed" | "paused";
  message: string;
}

function ErrorText({ message }: { message?: string }) {
  return message ? <small className="lb-field-error">{message}</small> : null;
}

export function BillingLandingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [verification, setVerification] = useState<VerifiedOtp>(EMPTY_VERIFICATION);
  const [otpSent, setOtpSent] = useState<OtpRequestResponse | null>(null);
  const [verificationError, setVerificationError] = useState("");
  const otpCooldown = useOtpCooldown();
  const form = useForm<BillingSignupValues>({
    resolver: zodResolver(billingSignupSchema),
    defaultValues: {
      name: "",
      business_name: "",
      phone: "",
      email: "",
      email_otp: "",
      password: "",
      terms_accepted: false,
      privacy_acknowledged: false,
      dpa_authority_accepted: false,
    },
  });
  const [email = "", password = ""] = useWatch({ control: form.control, name: ["email", "password"] });
  const normalizedEmail = normalizeEmail(email);
  const sentForEmail = otpSent?.destination === normalizedEmail;
  const verifiedForEmail = Boolean(verification.token && verification.destination === normalizedEmail);

  useEffect(() => {
    document.title = "LeadCat Billing | Simple billing for Indian businesses";
  }, []);

  const legalDocuments = useQuery({
    queryKey: ["legal-documents", "current"],
    queryFn: () => apiRequest<CurrentLegalDocumentsResponse>("/api/legal/documents/current"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const signupStatus = useQuery({
    queryKey: ["simple-billing-signup-status"],
    queryFn: () => apiRequest<BillingSignupStatus>(
      "/api/auth/signup/simple-billing/status",
    ),
    staleTime: 60 * 1000,
    retry: false,
  });
  const requestOtp = useMutation({
    mutationFn: () => apiRequest<OtpRequestResponse>("/api/auth/otp/request", {
      method: "POST",
      body: JSON.stringify({
        channel: "email",
        destination: normalizedEmail,
        workspace_product: "SIMPLE_BILLING",
      }),
    }),
    onSuccess: (result) => {
      setOtpSent(result);
      otpCooldown.start(result.resend_available_in_seconds);
      setVerification(EMPTY_VERIFICATION);
      setVerificationError("");
    },
  });
  const verifyOtp = useMutation({
    mutationFn: () => apiRequest<OtpVerifyResponse>("/api/auth/otp/verify", {
      method: "POST",
      body: JSON.stringify({
        channel: "email",
        destination: normalizedEmail,
        code: String(form.getValues("email_otp") || "").replace(/\D/g, ""),
        workspace_product: "SIMPLE_BILLING",
      }),
    }),
    onSuccess: (result) => {
      setVerification({ destination: result.destination, token: result.verification_token });
      setVerificationError("");
    },
  });
  const signup = useMutation({
    mutationFn: (payload: BillingSignupPayload) => apiRequest<AuthResponse>("/api/auth/signup/simple-billing", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
    onSuccess: (result) => {
      queryClient.clear();
      setStoredToken(result.token);
      navigate("/subscription", {
        replace: true,
        state: { authNotice: "Your LeadCat Billing workspace is ready. Choose monthly or annual billing to activate it." },
      });
    },
  });

  const sendOtp = () => {
    setVerificationError("");
    requestOtp.reset();
    verifyOtp.reset();
    if (!isValidEmail(normalizedEmail)) {
      setVerificationError("Enter a valid email address before sending OTP");
      return;
    }
    setVerification(EMPTY_VERIFICATION);
    requestOtp.mutate();
  };

  const verifyEmail = () => {
    setVerificationError("");
    verifyOtp.reset();
    if (String(form.getValues("email_otp") || "").replace(/\D/g, "").length !== 6) {
      setVerificationError("Enter the 6-digit OTP");
      return;
    }
    verifyOtp.mutate();
  };

  const submit = (values: BillingSignupValues) => {
    setVerificationError("");
    signup.reset();
    const normalizedSignupEmail = normalizeEmail(values.email);
    if (!verification.token || verification.destination !== normalizedSignupEmail) {
      setVerificationError("Please verify your email OTP before creating the Billing workspace");
      return;
    }
    const documents = legalDocuments.data?.documents || [];
    if (documents.length !== 3) {
      setVerificationError("Current legal documents could not be verified. Reload the page and try again.");
      return;
    }
    signup.mutate({
      name: values.name,
      business_name: values.business_name,
      password: values.password,
      email: normalizedSignupEmail,
      phone: normalizePhone(values.phone),
      email_verification_token: verification.token,
      legal_acceptance: {
        documents: documents.map((document) => ({
          document_type: document.document_type,
          version: document.version,
          content_hash_sha256: document.content_hash_sha256,
          acceptance_action: document.acceptance_action,
          confirmed: true,
          authority_confirmed: document.requires_authority,
        })),
      },
    });
  };

  const currentDocument = (type: "terms" | "privacy" | "dpa") => legalDocuments.data?.documents.find((item) => item.document_type === type);
  const terms = currentDocument("terms");
  const privacy = currentDocument("privacy");
  const dpa = currentDocument("dpa");
  const otpBusy = requestOtp.isPending || verifyOtp.isPending;
  const otpError = requestOtp.isError ? authError(requestOtp.error) : verifyOtp.isError ? authError(verifyOtp.error) : verificationError;

  return (
    <main className="lb-site">
      <header className="lb-header">
        <Link className="lb-brand" to="/"><span>LB</span><strong>LeadCat Billing</strong></Link>
        <nav aria-label="Billing public navigation">
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <Link to="/login" state={{ from: "/subscription" }}>Sign in</Link>
          <a className="lb-header-action" href="#billing-signup">Create workspace</a>
        </nav>
      </header>

      <section className="lb-hero" aria-labelledby="lb-title">
        <div className="lb-hero-copy">
          <span className="lb-eyebrow">Simple billing software</span>
          <h1 id="lb-title">Create professional bills. Keep every sale organized.</h1>
          <p>LeadCat Billing gives growing Indian businesses a focused workspace for products, permanent-numbered bills, PDFs, and sales summaries—without CRM clutter.</p>
          <div className="lb-hero-actions"><a href="#billing-signup">Create your Billing workspace</a><a href="#features">See what is included</a></div>
          <div className="lb-proof"><span>✓ Owner + one staff</span><span>✓ Clear subscription options</span><span>✓ Tenant-private records</span></div>
        </div>
        <div className="lb-preview" aria-label="LeadCat Billing bill preview">
          <div className="lb-preview-bar"><span>LeadCat Billing</span><em>New bill</em></div>
          <div className="lb-preview-head"><div><small>Bill to</small><strong>Acme Retail</strong></div><b>LCB-2026-0042</b></div>
          <div className="lb-preview-row heading"><span>Item</span><span>Qty</span><span>Amount</span></div>
          <div className="lb-preview-row"><span>Premium service</span><span>1</span><span>₹4,500</span></div>
          <div className="lb-preview-row"><span>Delivery</span><span>1</span><span>₹500</span></div>
          <div className="lb-preview-total"><span>Total</span><strong>₹5,000</strong></div>
          <div className="lb-preview-note">Permanent bill number · PDF ready</div>
        </div>
      </section>

      <section className="lb-features" id="features" aria-labelledby="lb-features-title">
        <div><span>Built for daily billing</span><h2 id="lb-features-title">Everything the counter and owner need.</h2></div>
        <div className="lb-feature-grid">
          <article><b>01</b><h3>Fast bill creation</h3><p>Choose saved products and rates, add customer details, and issue a clear bill.</p></article>
          <article><b>02</b><h3>Reliable history</h3><p>Keep permanent bill numbers and historical records available inside your tenant.</p></article>
          <article><b>03</b><h3>PDFs and reports</h3><p>Download polished PDFs and review sales totals without entering the CRM.</p></article>
        </div>
      </section>

      <section className="lb-onboarding" id="pricing">
        <div className="lb-offer">
          <span>One plan · two billing options</span>
          <h2>LeadCat Billing Standard</h2>
          <div className="lb-offer-prices" aria-label="Monthly and annual billing options">
            <div className="lb-offer-price-option">
              <span>Monthly</span>
              <div><strong>₹799</strong><small>per month</small></div>
            </div>
            <div className="lb-offer-price-option">
              <span>Annual</span>
              <div><strong>₹7,990</strong><small>per year</small></div>
            </div>
          </div>
          <ul><li>2 seats: owner + one staff</li><li>Simple Billing only</li><li>Private Billing workspace</li><li>Monthly or annual subscription options</li></ul>
          <p>Creating the workspace does not start a charge. Subscription activation follows separately.</p>
        </div>

        <section className="lb-signup" id="billing-signup" aria-labelledby="lb-signup-title">
          <span>Get started</span><h2 id="lb-signup-title">Create your Billing workspace</h2><p>Verify the owner email and set up a private Billing-only account.</p>
          {signupStatus.data && signupStatus.data.mode !== "public" ? (
            <InlineNotice
              tone={signupStatus.data.mode === "paused" ? "warning" : "info"}
              title={signupStatus.data.mode === "paused" ? "Signup paused" : "Controlled rollout"}
            >
              {signupStatus.data.message}
            </InlineNotice>
          ) : null}
          {signupStatus.isError ? <InlineNotice tone="error" title="Signup status unavailable">The signup gate could not be checked. The server will still enforce the current rollout policy.</InlineNotice> : null}
          {otpError ? <InlineNotice tone="error" title="Email verification">{otpError}</InlineNotice> : null}
          {legalDocuments.isError ? <InlineNotice tone="error" title="Legal documents unavailable">{authError(legalDocuments.error)}</InlineNotice> : null}
          {signup.isError ? <InlineNotice tone="error" title="Unable to create workspace">{authError(signup.error)}</InlineNotice> : null}
          <form noValidate onSubmit={form.handleSubmit(submit)}>
            <div className="lb-form-grid">
              <label>Owner name<input autoComplete="name" {...form.register("name")} /><ErrorText message={form.formState.errors.name?.message} /></label>
              <label>Business name<input autoComplete="organization" {...form.register("business_name")} /><ErrorText message={form.formState.errors.business_name?.message} /></label>
              <label>Phone number<input type="tel" autoComplete="tel" placeholder="+91 98765 43210" {...form.register("phone")} /><ErrorText message={form.formState.errors.phone?.message} /></label>
              <label>Email address<input type="email" autoComplete="email" {...form.register("email")} /><ErrorText message={form.formState.errors.email?.message} /></label>
            </div>
            <div className={`lb-otp ${verifiedForEmail ? "verified" : ""}`} role="group" aria-label="Verify owner email">
              <Button disabled={otpBusy || (sentForEmail && otpCooldown.remainingSeconds > 0)} onClick={sendOtp}>
                {sentForEmail && otpCooldown.remainingSeconds > 0
                  ? `Resend in ${otpCooldown.remainingSeconds}s`
                  : sentForEmail ? "Resend OTP" : "Send OTP"}
              </Button>
              <input aria-label="Email OTP" inputMode="numeric" maxLength={6} placeholder="6-digit OTP" disabled={!sentForEmail || otpBusy || verifiedForEmail} {...form.register("email_otp")} />
              <Button variant="navy" loading={verifyOtp.isPending} disabled={!sentForEmail || otpBusy || verifiedForEmail} onClick={verifyEmail}>{verifiedForEmail ? "Verified" : "Verify"}</Button>
              <small>{verifiedForEmail ? "Email verified" : sentForEmail ? `${otpSent?.message || "OTP sent"}${otpSent?.debug_code ? ` Debug OTP: ${otpSent.debug_code}` : ""}` : "Email verification is required"}</small>
            </div>
            <div className="lb-password"><label htmlFor="billing-password">Password</label><PasswordInput id="billing-password" autoComplete="new-password" {...form.register("password")} /><ErrorText message={form.formState.errors.password?.message} /><div className="lb-password-rules">{passwordRules(password).map((rule) => <em className={rule.ok ? "ok" : ""} key={rule.key}>{rule.label}</em>)}</div></div>
            <fieldset className="lb-legal" disabled={legalDocuments.isPending || legalDocuments.isError}>
              <legend>Legal confirmation</legend>
              {legalDocuments.isPending ? <p>Loading current legal documents…</p> : null}
              <label><input type="checkbox" {...form.register("terms_accepted")} /><span>I accept the <a href={terms?.published_url || "/terms.html"} target="_blank" rel="noreferrer">Terms of Service v{terms?.version || "1.1"}</a>.</span></label><ErrorText message={form.formState.errors.terms_accepted?.message} />
              <label><input type="checkbox" {...form.register("privacy_acknowledged")} /><span>I acknowledge the <a href={privacy?.published_url || "/privacy.html"} target="_blank" rel="noreferrer">Privacy Policy v{privacy?.version || "1.1"}</a>.</span></label><ErrorText message={form.formState.errors.privacy_acknowledged?.message} />
              <label><input type="checkbox" {...form.register("dpa_authority_accepted")} /><span>I am authorized to act for this business and accept the <a href={dpa?.published_url || "/dpa.html"} target="_blank" rel="noreferrer">Data Processing Addendum v{dpa?.version || "1.1"}</a>.</span></label><ErrorText message={form.formState.errors.dpa_authority_accepted?.message} />
            </fieldset>
            <Button className="lb-submit" variant="primary" type="submit" loading={signup.isPending}>Create Billing workspace</Button>
            <p className="lb-signin">Already have an account? <Link to="/login" state={{ from: "/subscription" }}>Sign in</Link></p>
          </form>
        </section>
      </section>

      <footer className="lb-footer"><Link to="/">Staminal Technologies</Link><span>LeadCat Billing · Simple, focused billing</span></footer>
    </main>
  );
}
